import { useEffect, useMemo, useRef, useState } from "react";
import type { PrivacyClassifyConfig, PrivacyLevel, PrivacyRoomItem, PrivacyTag } from "../scenario/types";
import { audio } from "../lib/audio";

export type PrivacyClassifyResult = {
  correct: number;
  total: number;
  missedIds: string[];
};

const TAG_META: Record<PrivacyTag, string> = {
  personal: "Personal data",
  financial: "Financial data",
  health: "Health data",
  credentials: "Credentials",
  legal: "Legal data",
  ip: "Intellectual property",
};

/** Shuffle once per mount, deterministic-ish (Fisher-Yates on a copy). */
function shuffled<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

type Placement = { level: PrivacyLevel; correct: boolean };

function CheckGlyph() {
  return (
    <svg viewBox="0 0 20 20" width="14" height="14" aria-hidden="true">
      <path d="M4 10.5 8 14.5 16 5.5" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function CrossGlyph() {
  return (
    <svg viewBox="0 0 20 20" width="14" height="14" aria-hidden="true">
      <path d="M5 5 15 15 M15 5 5 15" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
    </svg>
  );
}
function DashGlyph() {
  return (
    <svg viewBox="0 0 20 20" width="14" height="14" aria-hidden="true">
      <path d="M5 10h10" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
    </svg>
  );
}

/**
 * Episode 3, Phase B — drag-and-drop classification game over the SAME ten
 * items found in Phase A (`environment.privacyRoom.items`). The learner
 * drags each card into one of the four {@link PrivacyLevel} buckets;
 * implemented with pointer events (not native HTML5 DnD, which is flaky on
 * touch) so it works identically with mouse and touch. Every drop gets an
 * immediate, explanatory reveal (never a harsh "WRONG") — this is where the
 * learner is actually scored; Phase A never grades.
 *
 * Only sorting `ceil(items.length/2)` cards is mandatory — once that floor
 * is hit, a "Finish now" control appears alongside the tray so the learner
 * isn't forced through all ten. Anything left unsorted shows up in the
 * results table as "Not sorted" (counts against the score like a miss, but
 * is visually distinct from an active wrong guess).
 */
export function PrivacySort({
  config,
  items,
  onEnded,
}: {
  config: PrivacyClassifyConfig;
  items: PrivacyRoomItem[];
  onEnded: (result: PrivacyClassifyResult) => void;
}) {
  const deck = useMemo(() => shuffled(items), [items]);
  const [placed, setPlaced] = useState<Record<string, Placement>>({});
  const [revealId, setRevealId] = useState<string | null>(null);
  const [drag, setDrag] = useState<{ id: string; x: number; y: number; overCat: string | null } | null>(null);
  const [showHelp, setShowHelp] = useState(false);
  const [quickMode, setQuickMode] = useState(false);
  // Only half the deck is mandatory (see PrivacySort's docblock) — once the
  // learner hits that floor, "Finish now" lets them submit without sorting
  // every remaining card. Unsorted cards still show up in the results table
  // as "Not sorted" and count against the score, so there's no incentive to
  // finish early purely to dodge hard items, but nobody is stuck forever.
  const [finishedEarly, setFinishedEarly] = useState(false);
  const spoke = useRef(false);
  const zoneRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const dragRef = useRef<typeof drag>(null);
  dragRef.current = drag;

  useEffect(() => {
    if (spoke.current) return;
    spoke.current = true;
    const line =
      config.body ??
      "Drag each card into the level it really belongs to. There's no penalty for guessing.";
    void audio.speak(line, { voice: "onyx" });
    return () => audio.cancelSpeech();
  }, [config]);

  const remaining = deck.filter((i) => !placed[i.id]);
  const revealItem = items.find((i) => i.id === revealId) ?? null;
  const placedCount = items.length - remaining.length;
  const minRequired = Math.max(1, Math.ceil(items.length / 2));
  const meetsThreshold = placedCount >= minRequired;
  const showResults = !revealItem && (remaining.length === 0 || finishedEarly);

  const zoneAt = (x: number, y: number): string | null => {
    for (const cat of config.categories) {
      const el = zoneRefs.current[cat.id];
      if (!el) continue;
      const r = el.getBoundingClientRect();
      if (x >= r.left && x <= r.right && y >= r.top && y <= r.bottom) return cat.id;
    }
    return null;
  };

  const startDrag = (item: PrivacyRoomItem, clientX: number, clientY: number) => {
    if (revealItem) return;
    setDrag({ id: item.id, x: clientX, y: clientY, overCat: null });
  };

  useEffect(() => {
    if (!drag) return;
    const onMove = (e: PointerEvent) => {
      const overCat = zoneAt(e.clientX, e.clientY);
      setDrag((d) => (d ? { ...d, x: e.clientX, y: e.clientY, overCat } : d));
    };
    const onUp = (e: PointerEvent) => {
      const d = dragRef.current;
      if (!d) return;
      const overCat = zoneAt(e.clientX, e.clientY);
      setDrag(null);
      if (!overCat) return;
      const item = items.find((i) => i.id === d.id);
      if (!item) return;
      const correct = item.level === overCat;
      setPlaced((prev) => ({ ...prev, [item.id]: { level: overCat as PrivacyLevel, correct } }));
      if (quickMode) {
        // Quick mode: no per-item reveal card — just a chime and straight on
        // to the next card. The full breakdown table appears once at the end.
        if (correct) audio.chimeGood();
        else audio.chimeBad();
      } else {
        setRevealId(item.id);
        window.setTimeout(() => {
          if (correct) audio.chimeGood();
          else audio.chimeBad();
        }, 60);
      }
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!drag]);

  const dismissReveal = () => {
    setRevealId(null);
  };

  const submit = () => {
    const missed = items.filter((i) => !placed[i.id]?.correct);
    onEnded({ correct: items.length - missed.length, total: items.length, missedIds: missed.map((m) => m.id) });
  };

  const draggedItem = drag ? items.find((i) => i.id === drag.id) ?? null : null;

  return (
    <div className="psort" aria-label="Sort what you found">
      <div className="psort-panel">
        <div className="psort-head">
          <div className="rw-kicker">{"Episode 3 \u2014 Office privacy"}</div>
          <div className="psort-head-row">
            <h2>{config.headline ?? "Sort what you found"}</h2>
            <button
              type="button"
              className="psort-help-btn"
              onClick={() => setShowHelp(true)}
              title="What do these levels mean?"
            >
              <svg viewBox="0 0 24 24" fill="none" aria-hidden focusable="false">
                <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.7" />
                <path d="M9.4 9.2a2.7 2.7 0 0 1 5 1.2c0 1.7-2.4 2-2.4 3.4M12 17h.01" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              What do the levels mean?
            </button>
          </div>
          <p className="rw-body">
            {config.body ??
              "Drag each card into the level it really belongs to. There's no penalty for guessing \u2014 every card explains itself once you drop it."}
          </p>
        </div>

        <div className="psort-zones">
          {config.categories.map((cat) => (
            <div
              key={cat.id}
              ref={(el) => {
                zoneRefs.current[cat.id] = el;
              }}
              className={`psort-zone psort-zone--${cat.id} ${drag?.overCat === cat.id ? "over" : ""}`}
            >
              <div className="psort-zone-label">{cat.label}</div>
              {cat.hint && <div className="psort-zone-hint">{cat.hint}</div>}
              <div className="psort-zone-count">
                {items.filter((i) => placed[i.id]?.level === cat.id).length} placed
              </div>
            </div>
          ))}
        </div>

        {/* Single scroll region for whichever variable-height body is active
            (reveal / done-results / tray) — head+zones above stay pinned via
            flex:none on .psort-panel, and .psort-panel itself no longer
            scrolls (overflow:hidden), so this is the ONLY scrollbar the panel
            can ever show instead of an outer panel scrollbar nested around
            an inner .psort-results-table one. */}
        <div className="psort-scroll">
        {revealItem && (
          <div className="psort-reveal">
            <div className="psort-reveal-row">
              <span className="psort-reveal-icon" aria-hidden>
                {revealItem.icon}
              </span>
              <span className="psort-reveal-label">{revealItem.label}</span>
              <span className={`psort-reveal-badge ${placed[revealItem.id]?.correct ? "on" : "off"}`}>
                {placed[revealItem.id]?.correct ? <CheckGlyph /> : <CrossGlyph />}
                {placed[revealItem.id]?.correct ? "Correct" : "Incorrect"}
              </span>
            </div>
            {!placed[revealItem.id]?.correct && (
              <p className="psort-reveal-yourguess">
                You placed it in{" "}
                <strong>{config.categories.find((c) => c.id === placed[revealItem.id]?.level)?.label}</strong>.
              </p>
            )}
            <p className="psort-reveal-correct">
              Really: <strong>{config.categories.find((c) => c.id === revealItem.level)?.label}</strong>
              {revealItem.tags && revealItem.tags.length > 0 && (
                <span className="psort-reveal-tags">
                  {revealItem.tags.map((t) => (
                    <span key={t} className="psort-tag">
                      {TAG_META[t]}
                    </span>
                  ))}
                </span>
              )}
            </p>
            <p className="psort-reveal-explain">{revealItem.explain}</p>
            <div className="psort-reveal-footer">
              <label className="psort-quickmode psort-quickmode--inline">
                <input
                  type="checkbox"
                  checked={quickMode}
                  onChange={(e) => setQuickMode(e.target.checked)}
                />
                Quick mode &mdash; skip reveals, show results at the end
              </label>
              <button type="button" className="btn psort-next" onClick={dismissReveal}>
                {remaining.length > 0 ? "Next item" : "See results"}
              </button>
            </div>
          </div>
        )}

        {showResults && (
          <div className="psort-done">
            <p>
              Sorted {placedCount} of {items.length} items &mdash;{" "}
              {items.filter((i) => placed[i.id]?.correct).length} matched the right level.
            </p>
            <div className="psort-results-table" role="table" aria-label="Your classification results">
              <div className="psort-rt-row psort-rt-head" role="row">
                <span className="psort-rt-cell psort-rt-item" role="columnheader">Item</span>
                <span className="psort-rt-cell psort-rt-status" role="columnheader">Your answer</span>
                <span className="psort-rt-cell psort-rt-explain" role="columnheader">Why</span>
              </div>
              {items.map((item) => {
                const p = placed[item.id];
                const attempted = !!p;
                const ok = !!p?.correct;
                const realLabel = config.categories.find((c) => c.id === item.level)?.label ?? item.level;
                const yourLabel = p ? config.categories.find((c) => c.id === p.level)?.label ?? p.level : null;
                return (
                  <div key={item.id} className={`psort-rt-row ${ok ? "ok" : attempted ? "bad" : "skip"}`} role="row">
                    <span className="psort-rt-cell psort-rt-item" role="cell">
                      <span className="psort-rt-icon" aria-hidden>{item.icon}</span>
                      <span className="psort-rt-label">{item.label}</span>
                    </span>
                    <span className="psort-rt-cell psort-rt-status" role="cell">
                      <span className={`psort-rt-badge ${ok ? "on" : attempted ? "off" : "skip"}`}>
                        {ok ? <CheckGlyph /> : attempted ? <CrossGlyph /> : <DashGlyph />}
                        {ok ? "Correct" : attempted ? "Incorrect" : "Not sorted"}
                      </span>
                      {!ok && yourLabel && (
                        <span className="psort-rt-yours">
                          You said {yourLabel} &middot; really {realLabel}
                        </span>
                      )}
                    </span>
                    <span className="psort-rt-cell psort-rt-explain" role="cell">{item.explain}</span>
                  </div>
                );
              })}
            </div>
            <button type="button" className="btn psort-submit" onClick={submit}>
              Continue
            </button>
          </div>
        )}

        {!revealItem && !showResults && (
          <div className="psort-tray">
            <div className="psort-tray-label-row">
              <div className="psort-tray-label">Drag a card into a level above ({remaining.length} left)</div>
              {meetsThreshold && (
                <button
                  type="button"
                  className="btn psort-finish-early"
                  onClick={() => setFinishedEarly(true)}
                  title="You've sorted enough to move on &mdash; the rest will show as not sorted."
                >
                  Finish now ({placedCount}/{items.length} sorted)
                </button>
              )}
            </div>
            <div className="psort-tray-list">
              {remaining.map((item) => (
                <div
                  key={item.id}
                  className={`psort-card ${drag?.id === item.id ? "dragging-source" : ""}`}
                  onPointerDown={(e) => {
                    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
                    startDrag(item, e.clientX, e.clientY);
                  }}
                >
                  <span className="psort-card-icon" aria-hidden>
                    {item.icon}
                  </span>
                  <span className="psort-card-label">{item.label}</span>
                  {item.tags && item.tags.length > 0 && (
                    <span className="psort-card-tags">
                      {item.tags.map((t) => (
                        <span key={t} className="psort-card-tag">
                          {TAG_META[t]}
                        </span>
                      ))}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        </div>
      </div>

      {draggedItem && drag && (
        <div
          className="psort-drag-ghost"
          style={{ left: drag.x, top: drag.y }}
          aria-hidden
        >
          <span className="psort-card-icon">{draggedItem.icon}</span>
          <span className="psort-card-label">{draggedItem.label}</span>
        </div>
      )}

      {showHelp && (
        <div className="psort-help" role="dialog" aria-label="Data classification levels explained">
          <div className="psort-help-card">
            <div className="psort-help-head">
              <h3>How to classify information</h3>
              <button type="button" className="psort-help-close" onClick={() => setShowHelp(false)} aria-label="Close">
                {"\u2715"}
              </button>
            </div>
            <p className="psort-help-lede">
              Most companies use the same four-level ladder. The <strong>level</strong> answers
              <em> who is allowed to see it</em>; a secondary <strong>tag</strong> answers
              <em> what kind of data it is</em>.
            </p>
            <ul className="psort-help-levels">
              <li className="psort-help-lvl psort-help-lvl--public">
                <span className="psort-help-lvl-name">Public</span>
                <span className="psort-help-lvl-desc">Already meant for anyone &mdash; brochures, published prices, the public website. No harm if an outsider sees it.</span>
              </li>
              <li className="psort-help-lvl psort-help-lvl--internal">
                <span className="psort-help-lvl-name">Internal</span>
                <span className="psort-help-lvl-desc">For staff, but not secret &mdash; team notices, an office-event email, an org chart. Awkward, not damaging, if it leaks.</span>
              </li>
              <li className="psort-help-lvl psort-help-lvl--confidential">
                <span className="psort-help-lvl-name">Confidential</span>
                <span className="psort-help-lvl-desc">Would hurt a person, a customer or the business if an outsider saw it &mdash; a CV, a contract, deal notes. Often looks like an ordinary printout.</span>
              </li>
              <li className="psort-help-lvl psort-help-lvl--restricted">
                <span className="psort-help-lvl-name">Highly Confidential / Restricted</span>
                <span className="psort-help-lvl-desc">Grants access on its own (a password, a badge, an ID document) or carries the strongest legal protection (health data). The fewest people should ever see it.</span>
              </li>
            </ul>
            <div className="psort-help-tags">
              <div className="psort-help-tags-title">Tags &mdash; the <em>kind</em> of data (on top of the level)</div>
              <div className="psort-help-tags-list">
                {Object.values(TAG_META).map((t) => (
                  <span key={t} className="psort-tag">{t}</span>
                ))}
              </div>
              <p className="psort-help-tip">
                When you&apos;re unsure, treat it as <strong>Confidential</strong> until someone tells you otherwise &mdash;
                anything that grants access (a credential) jumps straight to <strong>Restricted</strong>.
              </p>
            </div>
            <button type="button" className="btn psort-help-done" onClick={() => setShowHelp(false)}>
              Got it
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
